<script setup lang="ts">
import { explainMonsterAffix } from '@/core/monster/affix'
import { useUserStore } from '@/stores/modules/user'
import type { PersonMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import EternalTowerRank from './EternalTowerRank.vue'
import { ref } from 'vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { useCommonStore } from '@/stores/modules/common'

const commonStore = useCommonStore()
const userStore = useUserStore()

defineProps<{ eternalMonster?: PersonMonsterType }>()
const emits = defineEmits(['start', 'sweep'])

function handleStartEternalTower(isAuto = false) {
  if (userStore.eternalTowerTimes <= 0) return ElMessage.warning('本层挑战次数已耗尽')
  if (userStore.soulType > 3) return ElMessage.warning('二次觉醒后无法参与不朽之塔')
  const curTime = dayjs()
  const week = curTime.day()
  if (week === 6) {
    const hour = curTime.hour()
    if (hour === 23) {
      const min = curTime.minute()
      if (min >= 55) return ElMessage.warning('不朽之塔结算中，无法进入挑战')
    }
  }
  ElMessageBox.confirm(
    `<p class="affix">确定进入【不朽之塔】挑战吗？（不论成败都将<span class="shrink">消耗</span>挑战次数）${
      userStore.eternalTowerTimes < 3 ? '<br /><b>【若消耗所有挑战次数，则本周无法再进行不朽之塔挑战】</b>' : ''
    }${isAuto ? '<br />您选择了【自动】挑战，将自动挑战至失败为止' : ''}</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'startEternalTower', data: { isAuto } })
      userStore.eternalTowerTimes--
      emits('start')
    })
    .catch(() => {})
}
function handleReceiveEternalTowerTimes() {
  ElMessageBox.confirm('<p class="affix">确定领取不朽之塔新手保护次数（3次）吗？<b class="shrink">一个账号永久只能领取 1次</b></p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync<PersonMonsterType>({ type: 'receiveEternalTowerProtect' })
      userStore.eternalTowerTimes = 3
      userStore.eternalTowerNoviceProtect = 0
    })
    .catch(() => {})
}
function handleSweepEternalTower() {
  if (userStore.eternalTowerLevel >= 40) return ElMessage.warning('您已通关不朽之塔')
  if (userStore.soulType > 3) return ElMessage.warning('二次觉醒后无法参与不朽之塔')
  ElMessageBox.confirm('<p class="affix">确定<span class="info">扫荡</span>不朽之塔吗？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      await socketEmitAsync({ type: 'sweepEternalTower' })
      userStore.eternalTowerTimes = 10
      userStore.eternalTowerLevel = 40
      ElMessage.success('扫荡成功，请在邮件处领取奖励')
      useCommonStore().getMailList()
      emits('sweep')
    })
    .catch(() => {})
}
const rankShow = ref(false)
</script>

<template>
  <div class="border-wrap eternal-tower">
    <h4 class="antiquity">
      不朽之塔
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>不朽之塔说明</h4>
            <p>1. 不朽之塔<span class="info">每周日0点刷新</span>，每次刷新塔内都将被随机的怪物占领，同时怪物的各项数值也是随机的。</p>
            <p>2. 每层仅有<span class="info">10次</span>挑战机会，进入则扣除次数（无论成败），若无次数则本周无法再挑战。</p>
            <p>3. 您需要在<span class="info">3分钟</span>的时间内通过挑战，进入后切装无效，请提前根据怪物属性搭配合理的装备。</p>
            <p>4. 奖励规则：</p>
            <p style="text-indent: 2em">
              a.
              <span class="gold">金币</span>
              奖励：1~10层每层奖励100,000金币；11~20层每层奖励60,000金币；21~30层每层奖励20,000金币；31~40层每层奖励200,000金币。
            </p>
            <p style="text-indent: 2em">
              b. <span class="antiquity">特殊</span>奖励：5、15、25、35层，将固定奖励 2 <span class="antiquity">远古石</span>；10层，将固定奖励 2
              <span class="legend">升星石</span>及 10 强化保护券；20层，将固定奖励 2 融合稳定剂及 5 强化保护券。
            </p>
            <p style="text-indent: 2em">
              c. <span class="info">随机</span>奖励：每层都有概率触发一次随机奖励：<span class="info">15%</span>概率 1 强化保护券；<span class="info"
                >10%</span
              >概率 1 <span class="antiquity">远古石</span>；<span class="info">5%</span>概率 1 <span class="legend">升星石</span>。
            </p>
          </div>
        </el-popover>
      </div>
    </h4>
    <p>
      <span style="margin-right: 15px">当前层级：{{ userStore.eternalTowerLevel + 1 }}</span>
      <span>剩余次数：{{ userStore.eternalTowerTimes }}</span>
    </p>
    <p>
      当前怪物：
      <el-popover placement="bottom" :width="300" trigger="click" popper-class="monster-detail" :teleported="false" :disabled="!eternalMonster">
        <template #reference>
          <el-button size="small" type="danger" plain>
            <span v-show="eternalMonster">{{ eternalMonster?.name }}</span>
            <span v-show="!eternalMonster">暂无战斗</span>
          </el-button>
        </template>
        <div class="monster-detail">
          <h3 style="margin: 5px 0">{{ eternalMonster?.name }}</h3>
          <p>血量：{{ eternalMonster?.hp }}</p>
          <p>防御：{{ eternalMonster?.def }}</p>
          <p>闪避率：{{ eternalMonster?.evs }}%</p>
          <p>抗爆率：{{ eternalMonster?.crtdef }}%</p>
          <template v-if="eternalMonster?.special">
            <p>特性：</p>
            <p v-for="item of explainMonsterAffix(eternalMonster.special)" v-html="item"></p>
          </template>
        </div>
      </el-popover>
    </p>
    <div style="margin: 10px 0">
      <el-button type="success" plain @click="handleStartEternalTower(false)"> 进入 </el-button>
      <el-button
        color="#ffb3a7"
        :dark="commonStore.isDarkMode"
        plain
        @click="handleStartEternalTower(true)"
        :disabled="userStore.dailyChallengeLevel.equip < 5"
        v-show="userStore.advanceLevel !== 4"
        >自动</el-button
      >
      <el-button color="#f7c99c" :dark="commonStore.isDarkMode" plain @click="handleSweepEternalTower" v-show="userStore.advanceLevel === 4"
        >扫荡</el-button
      >
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
    </div>
    <el-button
      type="warning"
      plain
      v-show="userStore.eternalTowerNoviceProtect === 1 && userStore.eternalTowerTimes === 0"
      @click="handleReceiveEternalTowerTimes"
    >
      领取新手保护次数
    </el-button>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <EternalTowerRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.eternal-tower {
  flex-flow: column;
}
</style>
